/*
 * Copyright (c) 2023 Roboto Technologies, Inc.
 */
import DeleteIcon from "@mui/icons-material/Delete";
import {
  Alert,
  Box,
  Paper,
  Skeleton,
  Table,
  TableBody,
  TableContainer,
  TableHead,
  TableRow,
  useTheme,
} from "@mui/material";
import * as React from "react";

import { useDomainServices } from "@/providers/DomainServices";
import {
  RobotoIconButton,
  RobotoTableCell,
  RobotoTableRow,
  RobotoTypography,
} from "@/shared/components";
import { Token } from "@/shared/domain/tokens";

import { TokenSwitch } from "./TokenSwitch";

interface TokensTableProps {
  data?: Token[];
  loading: boolean;
  refetch: () => void;
  setErrorText: (arg: string) => void;
  noResultsComponent?: React.ReactNode;
}

export const TokensTable: React.FC<TokensTableProps> = ({
  data,
  loading,
  refetch,
  setErrorText,
  noResultsComponent,
}) => {
  const theme = useTheme();

  const { tokens } = useDomainServices();

  const onDelete = (tokenId: string) => {
    // Clear any prior error text
    setErrorText("");

    tokens
      .deleteToken(tokenId)
      .then(() => {
        refetch();
      })
      .catch((err: unknown) => {
        const error =
          err instanceof Error
            ? err
            : new Error("Failed to delete token", {
                cause: err,
              });
        setErrorText(error.message);
      });
  };

  const tableFromGetTokensResponse = (data?: Token[]) => {
    const tokens = data ? data : [];
    const now = new Date();

    const formatExpires = (token: Token): string => {
      if (!token.context) {
        return "Unknown";
      }

      const expires = new Date(token.context.expires);
      if (expires > now) {
        return `Expires ${expires.toDateString()}`;
      } else {
        return `Expired ${expires.toDateString()}`;
      }
    };

    const formatLastUsed = (token: Token): string => {
      if (!token.context || !token.context.last_used) {
        return "Never Used";
      }

      const lastUsed = new Date(token.context.last_used);
      const daysAgo = Math.floor(
        (now.getTime() - lastUsed.getTime()) / (1000 * 60 * 60 * 24),
      );

      if (daysAgo === 0) {
        return "Today";
      } else if (daysAgo === 1) {
        return "Yesterday";
      } else {
        return `${daysAgo} days ago`;
      }
    };

    const rows = tokens.map((token, idx) => {
      return (
        <RobotoTableRow key={`token-row-${idx}`}>
          <RobotoTableCell>{token.context.name}</RobotoTableCell>
          <RobotoTableCell>{formatExpires(token)}</RobotoTableCell>
          <RobotoTableCell>{formatLastUsed(token)}</RobotoTableCell>
          <RobotoTableCell>
            <TokenSwitch
              enabled={token.context.enabled}
              tokenId={token.context.token_id}
              setErrorText={setErrorText}
            />
            <RobotoIconButton
              onClick={() => {
                void onDelete(token.context.token_id);
              }}
              eventName={"DeleteTokenClicked"}
              eventProperties={{
                tokenId: token.context.token_id,
              }}
            >
              <DeleteIcon fontSize="small" />
            </RobotoIconButton>
          </RobotoTableCell>
        </RobotoTableRow>
      );
    });

    return (
      <TableContainer component={Paper} variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow>
              <RobotoTableCell>Name</RobotoTableCell>
              <RobotoTableCell>Expires</RobotoTableCell>
              <RobotoTableCell>Last Used</RobotoTableCell>
              <RobotoTableCell>Actions</RobotoTableCell>
            </TableRow>
          </TableHead>
          <TableBody>{rows}</TableBody>
        </Table>
      </TableContainer>
    );
  };

  const table = tableFromGetTokensResponse(data);

  return (
    <Box>
      <RobotoTypography
        variant="body1"
        sx={{
          marginBottom: theme.spacing(2),
        }}
      >
        Access tokens
      </RobotoTypography>

      {loading && !data ? (
        <Box>
          <Skeleton animation="wave" variant="rounded" height={50} />
        </Box>
      ) : (
        <>
          {data?.length === 0 ? (
            (noResultsComponent ?? (
              <Alert severity="info">
                You haven&apos;t created any access tokens yet. You can create
                one using the form below.
              </Alert>
            ))
          ) : (
            <>{table}</>
          )}
        </>
      )}
    </Box>
  );
};
